import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { TruncateLevelsDialog } from '@/components/molecules';
import { AppTopbar } from '@/components/organisms/AppTopbar';
import { ConfirmDialog } from '@/components/organisms/ConfirmDialog';
import { MobileAsideDrawer } from '@/components/organisms/MobileAsideDrawer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { Character } from '@/schemas/character';

// page-specific (FI-53 Sprint F iter. 3): `LevelUpTopbar` estratto in file
// separato per rispettare `max-lines: 400` di `LevelUpFlowPage.tsx`. Pattern
// coerente con `LevelUpStepSpellsParts.tsx` (PR 10) e
// `LevelUpStepMulticlassParts.tsx` (PR 9). Componente non riusabile fuori
// dal flow level-up.

export interface LevelUpTopbarProps {
  character: Character;
  nextLevel: number;
  classDefName: string;
  onCancel: () => void;
  mobileDrawerContent: ReactNode;
  mobileDrawerWatermarkUrl: string;
}

export function LevelUpTopbar({
  character,
  nextLevel,
  classDefName,
  onCancel,
  mobileDrawerContent,
  mobileDrawerWatermarkUrl,
}: LevelUpTopbarProps) {
  const fromLevel = character.level;
  return (
    <AppTopbar
      variant="levelup"
      brand={
        <span className="font-display flex items-center gap-2 text-[0.78rem] tracking-[0.08em] uppercase">
          {/* FI-53 Sprint F: drawer trigger su mobile sostituisce R brand.
           *  Variant "levelup" ha sfondo ink → trigger paper-bright/ink per
           *  contrasto invertito. */}
          <span className="min-[900px]:hidden">
            <MobileAsideDrawer
              title="Navigazione livello"
              triggerClassName="bg-paper-bright text-ink hover:opacity-80"
              watermarkUrl={mobileDrawerWatermarkUrl}
            >
              {mobileDrawerContent}
            </MobileAsideDrawer>
          </span>
          <span className="bg-paper-bright text-ink font-display grid h-7 w-7 place-items-center text-[1rem] font-bold max-[900px]:hidden">
            R
          </span>
          <span className="hidden max-[900px]:hidden sm:inline">
            Dungeon&nbsp;World&nbsp;<em className="font-normal opacity-65">Rebrewed</em>
          </span>
        </span>
      }
      center={
        <span className="inline-flex items-baseline gap-2 truncate">
          <strong className="font-serif">{character.name || 'Senza nome'}</strong>
          <em className="font-serif opacity-85">, {classDefName}</em>
          <span className="opacity-55">·</span>
          <span className="font-display text-[0.78rem] tracking-[0.08em]">
            L{fromLevel} → <strong className="font-bold">L{nextLevel}</strong>
          </span>
        </span>
      }
      actions={
        <>
          <span className="bg-paper-bright text-ink font-display hidden px-[0.7em] py-[0.35em] text-[0.72rem] tracking-[0.12em] uppercase sm:inline-block">
            ↑ Sali di livello
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="text-paper-bright hover:bg-white/10 hover:text-paper-bright"
          >
            Annulla
          </Button>
        </>
      }
    />
  );
}

/**
 * **FI-46 PR-B**: due `ConfirmDialog` del flow (cancel + truncate edit)
 * raggruppati in un singolo componente per ridurre la dimensione di
 * `LevelUpFlowInner`. La copy cambia in modalità edit vs create.
 */
export interface LevelUpExitDialogsProps {
  cancelOpen: boolean;
  onCancelOpenChange: (open: boolean) => void;
  onCancelConfirm: () => void;
  isEditMode: boolean;
  editLevel?: number | undefined;
  truncateOpen: boolean;
  onTruncateOpenChange: (open: boolean) => void;
  onTruncateConfirm: () => void;
  truncatedLevels: number;
}

export function LevelUpExitDialogs({
  cancelOpen,
  onCancelOpenChange,
  onCancelConfirm,
  isEditMode,
  editLevel,
  truncateOpen,
  onTruncateOpenChange,
  onTruncateConfirm,
  truncatedLevels,
}: LevelUpExitDialogsProps) {
  return (
    <>
      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={onCancelOpenChange}
        variant="destructive"
        title={
          isEditMode
            ? `Annullare la modifica al livello ${editLevel}?`
            : 'Annullare la salita di livello?'
        }
        description={
          isEditMode
            ? 'Le modifiche fatte finora andranno perse. Il personaggio resta come prima.'
            : 'Le scelte fatte finora andranno perse. Il personaggio resta al livello corrente.'
        }
        confirmLabel="Sì, annulla"
        cancelLabel="Resta qui"
        onConfirm={onCancelConfirm}
      />
      {isEditMode && editLevel !== undefined ? (
        <TruncateLevelsDialog
          open={truncateOpen}
          onOpenChange={onTruncateOpenChange}
          onConfirm={onTruncateConfirm}
          editLevel={editLevel}
          truncatedLevels={truncatedLevels}
        />
      ) : null}
    </>
  );
}

/**
 * **FI-46 PR-B**: empty state mostrato quando il route `:level` non è
 * valido (L1 = scope FI-46bis, fuori range, record mancante). CTA torna
 * al Hub.
 */
export function EditLevelInvalidState({ reason }: { reason: string }) {
  return (
    <main className="mx-auto max-w-3xl p-6">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/character">
          <ArrowLeft aria-hidden />
          Scheda personaggio
        </Link>
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>Modifica livello non disponibile</CardTitle>
          <CardDescription>{reason}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to="/character">Torna alla scheda</Link>
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
